/**
 * Minecraft-style Home splash: tilted left, breathe scale, text color only.
 * Outer host is anchored under the E; inner span holds text + breathe scale
 * so text length never shifts the pivot. Supports `[target]Label` interactive links.
 * Font size (and a final scale fallback) shrink so the line stays inside the viewport —
 * important because the splash is centered under the title E, not the screen center.
 */
import { fetchSplashTexts } from "../lib/cloud";
import { DEFAULT_SPLASH_TEXTS } from "../../lib/splashTexts";
import { parseSplashLine } from "./splash-links";

let pool: string[] = [...DEFAULT_SPLASH_TEXTS];
let el: HTMLElement | null = null;
let innerEl: HTMLElement | null = null;
let loaded = false;
let enabled = true;
let fitRaf = 0;
let fitRetryTimer = 0;
let resizeBound = false;

/** Peak of homeSplashBreathe scale — leave room so animation does not clip. */
const BREATHE_PAD = 1.06;
const EDGE_MARGIN_PX = 8;

export type SplashLinkNavigateFn = (target: string) => void;

let navigateSplashLink: SplashLinkNavigateFn | null = null;

export function setSplashLinkNavigator(fn: SplashLinkNavigateFn | null): void {
  navigateSplashLink = fn;
}

function pickRandom(list: string[]): string | null {
  if (!list.length) return null;
  return list[Math.floor(Math.random() * list.length)] ?? null;
}

function ensureEls(): HTMLElement | null {
  if (!el) el = document.getElementById("homeSplash");
  if (!el) return null;
  if (!innerEl || !el.contains(innerEl)) {
    innerEl = el.querySelector(".homeSplashInner");
    if (!innerEl) {
      innerEl = document.createElement("span");
      innerEl.className = "homeSplashInner";
      el.replaceChildren(innerEl);
    }
  }
  return el;
}

function currentText(): string {
  return (innerEl?.textContent || "").trim();
}

function viewportBox(): { left: number; top: number; right: number; bottom: number } {
  const vv = window.visualViewport;
  if (vv) {
    return {
      left: vv.offsetLeft,
      top: vv.offsetTop,
      right: vv.offsetLeft + vv.width,
      bottom: vv.offsetTop + vv.height,
    };
  }
  return { left: 0, top: 0, right: window.innerWidth, bottom: window.innerHeight };
}

/** True if the splash AABB (with breathe padding) stays inside the viewport margins. */
function splashFitsInViewport(node: HTMLElement, margin: number): boolean {
  const r = node.getBoundingClientRect();
  const cx = (r.left + r.right) / 2;
  const cy = (r.top + r.bottom) / 2;
  const hw = (r.width / 2) * BREATHE_PAD;
  const hh = (r.height / 2) * BREATHE_PAD;
  const box = viewportBox();
  return (
    cx - hw >= box.left + margin &&
    cx + hw <= box.right - margin &&
    cy - hh >= box.top + margin &&
    cy + hh <= box.bottom - margin
  );
}

function pauseBreatheForMeasure(): () => void {
  if (!innerEl) return () => {};
  const prevAnim = innerEl.style.animation;
  const prevTransform = innerEl.style.transform;
  innerEl.style.animation = "none";
  innerEl.style.transform = "scale(1)";
  return () => {
    if (!innerEl) return;
    innerEl.style.animation = prevAnim;
    innerEl.style.transform = prevTransform;
  };
}

/**
 * Shrink splash font so a long line stays on-screen.
 * Splash is anchored under the title E (often right of center), so we fit edges —
 * not only total width vs viewport.
 */
function fitHomeSplashToViewport(): void {
  if (!ensureEls() || !el) return;
  if (el.classList.contains("hidden") || !currentText()) {
    el.style.fontSize = "";
    el.style.setProperty("--home-splash-fit-scale", "1");
    return;
  }

  const restoreBreathe = pauseBreatheForMeasure();
  try {
    el.style.setProperty("--home-splash-fit-scale", "1");
    el.style.fontSize = "";
    void el.offsetWidth;

    const preferred = parseFloat(getComputedStyle(el).fontSize) || 16;
    const minPx = 6;
    const margin = EDGE_MARGIN_PX;

    if (splashFitsInViewport(el, margin)) {
      el.style.fontSize = "";
      return;
    }

    let lo = minPx;
    let hi = preferred;
    let best = minPx;
    for (let i = 0; i < 16; i++) {
      const mid = (lo + hi) / 2;
      el.style.fontSize = `${mid}px`;
      void el.offsetWidth;
      if (splashFitsInViewport(el, margin)) {
        best = mid;
        lo = mid;
      } else {
        hi = mid;
      }
    }
    el.style.fontSize = `${best}px`;
    void el.offsetWidth;

    if (splashFitsInViewport(el, margin)) return;

    // Still overflowing at minimum font — scale the whole splash down.
    const r = el.getBoundingClientRect();
    const box = viewportBox();
    const cx = (r.left + r.right) / 2;
    const cy = (r.top + r.bottom) / 2;
    const maxHalfW = Math.max(8, Math.min(cx - (box.left + margin), box.right - margin - cx));
    const maxHalfH = Math.max(8, Math.min(cy - (box.top + margin), box.bottom - margin - cy));
    const needHalfW = (r.width / 2) * BREATHE_PAD;
    const needHalfH = (r.height / 2) * BREATHE_PAD;
    const scale = Math.min(1, maxHalfW / needHalfW, maxHalfH / needHalfH);
    el.style.setProperty("--home-splash-fit-scale", String(Math.max(0.35, scale * 0.98)));
  } finally {
    restoreBreathe();
  }
}

function scheduleFitHomeSplash(): void {
  if (fitRaf) cancelAnimationFrame(fitRaf);
  fitRaf = requestAnimationFrame(() => {
    fitRaf = 0;
    fitHomeSplashToViewport();
    // Second pass after layout / web fonts settle (mobile Safari, etc.).
    if (fitRetryTimer) window.clearTimeout(fitRetryTimer);
    fitRetryTimer = window.setTimeout(() => {
      fitRetryTimer = 0;
      fitHomeSplashToViewport();
    }, 80);
  });
}

function bindSplashResize(): void {
  if (resizeBound || typeof window === "undefined") return;
  resizeBound = true;
  window.addEventListener("resize", scheduleFitHomeSplash, { passive: true });
  window.addEventListener("orientationchange", scheduleFitHomeSplash, { passive: true });
  window.visualViewport?.addEventListener("resize", scheduleFitHomeSplash, { passive: true });
  window.visualViewport?.addEventListener("scroll", scheduleFitHomeSplash, { passive: true });
}

function setSplashText(raw: string): void {
  if (!ensureEls() || !innerEl) return;
  innerEl.replaceChildren();
  const segments = parseSplashLine(raw);
  for (const seg of segments) {
    if (seg.type === "text") {
      if (seg.value) innerEl.appendChild(document.createTextNode(seg.value));
      continue;
    }
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "homeSplashLink";
    btn.textContent = seg.label;
    btn.dataset.splashTarget = seg.target;
    btn.setAttribute("aria-label", seg.label);
    btn.addEventListener("click", (e) => {
      e.preventDefault();
      e.stopPropagation();
      navigateSplashLink?.(seg.target);
    });
    innerEl.appendChild(btn);
  }
  scheduleFitHomeSplash();
}

export function syncHomeSplashEnabled(on: boolean): void {
  enabled = !!on;
  if (!ensureEls() || !el) return;
  if (!enabled) {
    el.classList.add("hidden");
    el.style.fontSize = "";
    el.style.setProperty("--home-splash-fit-scale", "1");
    return;
  }
  if (!currentText()) {
    applyHomeSplashText();
    return;
  }
  el.classList.remove("hidden");
  scheduleFitHomeSplash();
}

export function applyHomeSplashText(): void {
  if (!ensureEls() || !el) return;
  if (!enabled) {
    if (innerEl) innerEl.replaceChildren();
    el.classList.add("hidden");
    el.style.fontSize = "";
    el.style.setProperty("--home-splash-fit-scale", "1");
    return;
  }
  const text = pickRandom(pool);
  if (!text) {
    if (innerEl) innerEl.replaceChildren();
    el.classList.add("hidden");
    el.style.fontSize = "";
    el.style.setProperty("--home-splash-fit-scale", "1");
    return;
  }
  setSplashText(text);
  el.classList.remove("hidden");
  scheduleFitHomeSplash();
}

export async function initHomeSplash(): Promise<void> {
  if (!ensureEls()) return;
  bindSplashResize();
  applyHomeSplashText();
  try {
    const texts = await fetchSplashTexts();
    if (texts.length) pool = texts.map((t) => t.trim()).filter(Boolean);
  } catch {
    /* keep defaults */
  }
  loaded = true;
  applyHomeSplashText();
}

/** Call when returning to Home / restarting so a new splash is shown. */
export function refreshHomeSplash(): void {
  if (!enabled) {
    if (ensureEls() && el) {
      if (innerEl) innerEl.replaceChildren();
      el.classList.add("hidden");
      el.style.fontSize = "";
      el.style.setProperty("--home-splash-fit-scale", "1");
    }
    return;
  }
  if (!loaded && !pool.length) return;
  applyHomeSplashText();
}
